import {  t  } from "@/client/libs/dummy-i18n";
import { CrownIcon, FadersHorizontalIcon, ReadCvLogoIcon } from "@phosphor-icons/react";
import { Button, KeyboardShortcut } from "@stackmorph/ui";
import { cn } from "@stackmorph/utils";
import { motion } from "framer-motion";
import { Link, useLocation, useNavigate } from "react-router";
import useKeyboardShortcut from "use-keyboard-shortcut";

import { useUser } from "@/client/services/user";

type Props = {
  className?: string;
};

const ActiveIndicator = ({ className }: Props) => (
  <motion.div
    initial={{ opacity: 0, x: -20 }}
    animate={{ opacity: 1, x: 0 }}
    className={cn(
      "size-1.5 animate-pulse rounded-full bg-info shadow-[0_0_12px] shadow-info",
      className,
    )}
  />
);

type SidebarItem = {
  path: string;
  name: string;
  shortcut?: string;
  icon: React.ReactNode;
};

type SidebarItemProps = SidebarItem & {
  onClick?: () => void;
};

const SidebarItem = ({ path, name, shortcut, icon, onClick }: SidebarItemProps) => {
  const isActive = useLocation().pathname === path;

  return (
    <Button
      asChild
      size="lg"
      variant="ghost"
      className={cn(
        "h-auto justify-start px-4 py-3 rounded-xl transition-all duration-200",
        isActive 
          ? "bg-primary text-primary-foreground shadow-lg shadow-primary/20" 
          : "hover:bg-white/10 hover:text-foreground",
      )}
      onClick={onClick}
    >
      <Link to={path}>
        <div className="mr-3">{icon}</div>
        <span>{name}</span>
        {!isActive && <KeyboardShortcut className="ml-auto">{shortcut}</KeyboardShortcut>}
        {isActive && <ActiveIndicator className="ml-auto" />}
      </Link>
    </Button>
  );
};

type SidebarProps = {
  setOpen?: (open: boolean) => void;
};

export const Sidebar = ({ setOpen }: SidebarProps) => {
  const { user } = useUser();
  const navigate = useNavigate();
  const location = useLocation();

  useKeyboardShortcut(["shift", "r"], () => {
    void navigate("/dashboard/resumes");
    setOpen?.(false);
  });

  useKeyboardShortcut(["shift", "p"], () => {
    void navigate("/dashboard/premium");
    setOpen?.(false);
  });

  useKeyboardShortcut(["shift", "s"], () => {
    void navigate("/dashboard/settings");
    setOpen?.(false);
  });

  const sidebarItems: SidebarItem[] = [
    {
      path: "/dashboard/resumes",
      name: t`Resumes`,
      shortcut: "⇧R",
      icon: <ReadCvLogoIcon />,
    },
    {
      path: "/dashboard/premium",
      name: t`Premium`,
      shortcut: "⇧P",
      icon: <CrownIcon weight="fill" />,
    },
    {
      path: "/dashboard/settings",
      name: t`Settings`,
      shortcut: "⇧S",
      icon: <FadersHorizontalIcon />,
    },
  ];

  // Render as compact bottom nav buttons
  return (
    <>
      {sidebarItems.map((item) => {
        const isActive = location.pathname === item.path;
        return (
          <Button
            key={item.path}
            asChild
            size="sm"
            variant="ghost"
            className={cn(
              "flex-col gap-1 lg:flex-row lg:gap-2 lg:px-4 lg:py-2 rounded-full relative transition-all",
              isActive && "bg-primary text-primary-foreground"
            )}
            onClick={() => {
              void navigate(item.path);
              setOpen?.(false);
            }}
          >
            <div className="flex flex-col items-center lg:flex-row lg:gap-2">
              <div className={cn("text-xl lg:text-base", isActive && "scale-110")}>
                {item.icon}
              </div>
              <span className="text-xs lg:text-sm font-medium">{item.name}</span>
            </div>
          </Button>
        );
      })}
    </>
  );
};
